import { useCallback, useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { Loader2, Pencil, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { SupplierCategoryBadges } from '../suppliers/SupplierCategoryBadges';
import apiClient from '../../services/apiClient';
import { useCategoriesApi, useCategoryActions } from '../../services/categories';
import {
  useShippingLocationActions,
  useShippingLocationsApi,
} from '../../services/shippingLocations';
import { ROLE_LABELS, type UserRole } from '../../types/auth';
import { useAppSettings } from '../../hook/useAppSettings';

type SettingsTabKey =
  | 'company'
  | 'finances'
  | 'alerts'
  | 'masters'
  | 'users'
  | 'maintenance';

type CompanySettingsForm = {
  companyName: string;
  taxId: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  mercantileRegistry: string;
};

type FinanceSettingsForm = {
  currencyCode: string;
  currencyLocale: string;
  ivaRatePct: string;
};

type AlertRuleSettings = {
  enabled: boolean;
  thresholdValue: string;
};

type AlertSettingsForm = {
  orderEmailOverdue: AlertRuleSettings;
  orderCallOverdue: AlertRuleSettings;
  orderConfirmOverdue: AlertRuleSettings;
  subprojectPendingItemsOverdue: AlertRuleSettings;
  subprojectCostDeviationWatch: AlertRuleSettings;
  subprojectPendingOrdersWatch: AlertRuleSettings;
};

type SettingsUserRow = {
  id: string;
  name: string;
  apellidos: string;
  email: string;
  role: UserRole;
  active: boolean;
  created_at: string;
};

type MaintenanceProjectRow = {
  id: string;
  ref: string;
  name: string;
  client: string;
  status: string;
};

type MaintenanceSubprojectRow = {
  id: string;
  ref: string | null;
  name: string;
  type: string;
  status: string;
};

type MaintenanceSupplierRow = {
  id: string;
  name: string;
  email: string | null;
  category_ids: string[];
  categories: string[];
  active: boolean;
};

type MaintenanceDeleteTarget = {
  kind: 'project' | 'subproject' | 'supplier' | 'category' | 'shippingLocation';
  id: string;
  label: string;
};

type ProjectListResponse = {
  items: MaintenanceProjectRow[];
};

type SubprojectListResponse = MaintenanceSubprojectRow[];

type SupplierListResponse = {
  items: MaintenanceSupplierRow[];
};

type SettingsApiResponse = {
  company_name: string;
  tax_id: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  mercantile_registry: string;
  currency_code: string;
  currency_locale: string;
  iva_rate_pct: string | number;
  order_email_overdue_enabled: boolean;
  order_email_overdue_days: number;
  order_call_overdue_enabled: boolean;
  order_call_overdue_days: number;
  order_confirm_overdue_enabled: boolean;
  order_confirm_overdue_days: number;
  subproject_pending_items_overdue_enabled: boolean;
  subproject_pending_items_overdue_days: number;
  subproject_cost_deviation_watch_enabled: boolean;
  subproject_cost_deviation_watch_pct: number | string;
  subproject_pending_orders_watch_enabled: boolean;
  subproject_pending_orders_watch_limit: number;
  updated_at: string;
};

type UserStatusFilter = 'active' | 'all';
type MasterStatusFilter = 'active' | 'all';
type UserDialogMode = 'create' | 'edit';

type CurrencyOption = {
  code: string;
  locale: string;
  label: string;
};

type MasterDialogMode = 'create' | 'edit';

const DEFAULT_COMPANY_SETTINGS: CompanySettingsForm = {
  companyName: 'Estudio Interior SL',
  taxId: 'B12345678',
  address: 'Calle del Diseño 25, Madrid',
  phone: '+34 91 123 4567',
  email: 'info@estudiointerior.com',
  website: 'www.estudiointerior.com',
  mercantileRegistry: 'Madrid, Tomo 123, Folio 456',
};

const DEFAULT_FINANCE_SETTINGS: FinanceSettingsForm = {
  currencyCode: 'EUR',
  currencyLocale: 'es-ES',
  ivaRatePct: '21',
};

const CURRENCY_OPTIONS: CurrencyOption[] = [
  { code: 'EUR', locale: 'es-ES', label: 'Euro (€)' },
  { code: 'USD', locale: 'en-US', label: 'Dólar ($)' },
];

const DEFAULT_ALERT_SETTINGS: AlertSettingsForm = {
  orderEmailOverdue: { enabled: true, thresholdValue: '3' },
  orderCallOverdue: { enabled: true, thresholdValue: '3' },
  orderConfirmOverdue: { enabled: true, thresholdValue: '3' },
  subprojectPendingItemsOverdue: { enabled: true, thresholdValue: '5' },
  subprojectCostDeviationWatch: { enabled: true, thresholdValue: '10' },
  subprojectPendingOrdersWatch: { enabled: true, thresholdValue: '3' },
};

const MAINTENANCE_PAGE_SIZE = 10;

const STATUS_LABELS: Record<string, string> = {
  planificacion: 'Planificación',
  en_progreso: 'En progreso',
  presupuestado: 'Presupuestado',
  finalizado: 'Finalizado',
  borrador: 'Borrador',
  pedido: 'Pedido',
  confirmado: 'Confirmado',
  enviado: 'Enviado',
  recibido: 'Recibido',
  cancelado: 'Cancelado',
};

function formatStatusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => window.clearTimeout(timeoutId);
  }, [value, delay]);

  return debouncedValue;
}

export function Settings() {
  const { refreshSettings } = useAppSettings();
  const [activeTab, setActiveTab] = useState<SettingsTabKey>('company');

  const [companyForm, setCompanyForm] = useState<CompanySettingsForm>(DEFAULT_COMPANY_SETTINGS);
  const [financeForm, setFinanceForm] = useState<FinanceSettingsForm>(DEFAULT_FINANCE_SETTINGS);
  const [alertForm, setAlertForm] = useState<AlertSettingsForm>(DEFAULT_ALERT_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsHydrated, setSettingsHydrated] = useState(false);
  const [companySaving, setCompanySaving] = useState(false);
  const [financeSaving, setFinanceSaving] = useState(false);
  const [alertsSaving, setAlertsSaving] = useState(false);
  const [loadedIvaRatePct, setLoadedIvaRatePct] = useState(DEFAULT_FINANCE_SETTINGS.ivaRatePct);
  const [financeWarningOpen, setFinanceWarningOpen] = useState(false);

  const [users, setUsers] = useState<SettingsUserRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [usersStatusFilter, setUsersStatusFilter] = useState<UserStatusFilter>('active');
  const [userDialogMode, setUserDialogMode] = useState<UserDialogMode>('edit');
  const [editingUser, setEditingUser] = useState<SettingsUserRow | null>(null);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [userNameDraft, setUserNameDraft] = useState('');
  const [userApellidosDraft, setUserApellidosDraft] = useState('');
  const [userEmailDraft, setUserEmailDraft] = useState('');
  const [userPasswordDraft, setUserPasswordDraft] = useState('');
  const [userRoleDraft, setUserRoleDraft] = useState<UserRole>('trabajador');
  const [userActiveDraft, setUserActiveDraft] = useState(true);
  const [userSaving, setUserSaving] = useState(false);

  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projects, setProjects] = useState<MaintenanceProjectRow[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [projectsPage, setProjectsPage] = useState(1);

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [subprojects, setSubprojects] = useState<MaintenanceSubprojectRow[]>([]);
  const [subprojectsLoading, setSubprojectsLoading] = useState(false);
  const [subprojectsError, setSubprojectsError] = useState<string | null>(null);
  const [subprojectsPage, setSubprojectsPage] = useState(1);

  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [suppliers, setSuppliers] = useState<MaintenanceSupplierRow[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [suppliersError, setSuppliersError] = useState<string | null>(null);
  const [suppliersPage, setSuppliersPage] = useState(1);

  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [categoryStatusFilter, setCategoryStatusFilter] = useState<MasterStatusFilter>('active');
  const [shippingSearchTerm, setShippingSearchTerm] = useState('');
  const [shippingStatusFilter, setShippingStatusFilter] = useState<MasterStatusFilter>('active');
  const [categoriesHydrated, setCategoriesHydrated] = useState(false);
  const [shippingHydrated, setShippingHydrated] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryDialogMode, setCategoryDialogMode] = useState<MasterDialogMode>('create');
  const [categoryDialogId, setCategoryDialogId] = useState<string | null>(null);
  const [categoryDialogName, setCategoryDialogName] = useState('');
  const [categoryDialogActive, setCategoryDialogActive] = useState(true);
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [shippingDialogMode, setShippingDialogMode] = useState<MasterDialogMode>('create');
  const [shippingDialogId, setShippingDialogId] = useState<string | null>(null);
  const [shippingDialogName, setShippingDialogName] = useState('');
  const [shippingDialogActive, setShippingDialogActive] = useState(true);
  const [shippingSubmitting, setShippingSubmitting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<MaintenanceDeleteTarget | null>(null);
  const debouncedProjectSearchTerm = useDebounce(projectSearchTerm, 300);
  const debouncedSupplierSearchTerm = useDebounce(supplierSearchTerm, 300);
  const debouncedCategorySearchTerm = useDebounce(categorySearchTerm, 300);
  const debouncedShippingSearchTerm = useDebounce(shippingSearchTerm, 300);

  const {
    categories,
    loading: categoriesLoading,
    refresh: refreshCategories,
  } = useCategoriesApi({
    active: true,
    limit: 200,
  });
  const {
    shippingLocations,
    loading: shippingLocationsLoading,
    refresh: refreshShippingLocations,
  } = useShippingLocationsApi({
    active: true,
    limit: 200,
  });
  const { createCategory, updateCategory, deleteCategory } = useCategoryActions();
  const { createShippingLocation, updateShippingLocation, deleteShippingLocation } =
    useShippingLocationActions();
  const mastersHydrated = categoriesHydrated && shippingHydrated;

  const normalizeApiError = (error: unknown, fallback: string) => {
    if (isAxiosError(error)) {
      const detail =
        typeof error.response?.data === 'object' &&
        error.response?.data &&
        'detail' in error.response.data &&
        typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : null;
      return detail ?? fallback;
    }
    return fallback;
  };

  const applySettingsToForms = (settings: SettingsApiResponse) => {
    setCompanyForm({
      companyName: settings.company_name,
      taxId: settings.tax_id,
      address: settings.address,
      phone: settings.phone,
      email: settings.email,
      website: settings.website,
      mercantileRegistry: settings.mercantile_registry,
    });
    setFinanceForm({
      currencyCode: settings.currency_code,
      currencyLocale: settings.currency_locale,
      ivaRatePct: String(settings.iva_rate_pct),
    });
    setLoadedIvaRatePct(String(settings.iva_rate_pct));
    setAlertForm({
      orderEmailOverdue: {
        enabled: settings.order_email_overdue_enabled,
        thresholdValue: String(settings.order_email_overdue_days),
      },
      orderCallOverdue: {
        enabled: settings.order_call_overdue_enabled,
        thresholdValue: String(settings.order_call_overdue_days),
      },
      orderConfirmOverdue: {
        enabled: settings.order_confirm_overdue_enabled,
        thresholdValue: String(settings.order_confirm_overdue_days),
      },
      subprojectPendingItemsOverdue: {
        enabled: settings.subproject_pending_items_overdue_enabled,
        thresholdValue: String(settings.subproject_pending_items_overdue_days),
      },
      subprojectCostDeviationWatch: {
        enabled: settings.subproject_cost_deviation_watch_enabled,
        thresholdValue: String(settings.subproject_cost_deviation_watch_pct),
      },
      subprojectPendingOrdersWatch: {
        enabled: settings.subproject_pending_orders_watch_enabled,
        thresholdValue: String(settings.subproject_pending_orders_watch_limit),
      },
    });
  };

  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const response = await apiClient.get<SettingsApiResponse>('/settings');
      applySettingsToForms(response.data);
    } catch (error) {
      toast.error(normalizeApiError(error, 'No pudimos cargar la configuración global.'));
    } finally {
      setSettingsLoading(false);
      setSettingsHydrated(true);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const response = await apiClient.get<SettingsUserRow[]>('/users/list', {
        params: {
          limit: 50,
          offset: 0,
          active: usersStatusFilter === 'active' ? true : undefined,
        },
      });
      setUsers(response.data);
    } catch (error) {
      setUsersError(normalizeApiError(error, 'No pudimos cargar los usuarios.'));
    } finally {
      setUsersLoading(false);
    }
  }, [usersStatusFilter]);

  const fetchProjects = useCallback(
    async (search: string) => {
      setProjectsLoading(true);
      setProjectsError(null);
      try {
        const response = await apiClient.get<ProjectListResponse>('/projects/list', {
          params: {
            limit: 100,
            offset: 0,
            search: search.trim() || undefined,
          },
        });

        const nextProjects = response.data.items ?? [];
        setProjects(nextProjects);
        if (nextProjects.length === 0) {
          setSubprojects([]);
        }
        setSelectedProjectId((previous) => {
          if (nextProjects.length === 0) return '';
          if (!previous) return nextProjects[0].id;
          if (!nextProjects.some((project) => project.id === previous)) {
            return nextProjects[0].id;
          }
          return previous;
        });
      } catch {
        setProjectsError('No pudimos cargar proyectos para mantenimiento.');
      } finally {
        setProjectsLoading(false);
      }
    },
    [],
  );

  const fetchSubprojects = useCallback(async (projectId: string) => {
    if (!projectId) {
      setSubprojects([]);
      return;
    }

    setSubprojectsLoading(true);
    setSubprojectsError(null);
    try {
      const response = await apiClient.get<SubprojectListResponse>('/subprojects/list', {
        params: {
          project_id: projectId,
          limit: 100,
          offset: 0,
        },
      });
      setSubprojects(response.data ?? []);
    } catch {
      setSubprojectsError('No pudimos cargar subproyectos para mantenimiento.');
    } finally {
      setSubprojectsLoading(false);
    }
  }, []);

  const fetchSuppliers = useCallback(async (search: string) => {
    setSuppliersLoading(true);
    setSuppliersError(null);
    try {
      const response = await apiClient.get<SupplierListResponse>('/suppliers', {
        params: {
          limit: 100,
          offset: 0,
          search: search.trim() || undefined,
        },
      });
      setSuppliers(response.data.items ?? []);
    } catch {
      setSuppliersError('No pudimos cargar proveedores para mantenimiento.');
    } finally {
      setSuppliersLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    void fetchProjects('');
    void fetchSuppliers('');
  }, [fetchProjects, fetchSuppliers]);

  useEffect(() => {
    if (!selectedProjectId) {
      setSubprojects([]);
      return;
    }
    void fetchSubprojects(selectedProjectId);
  }, [selectedProjectId, fetchSubprojects]);

  useEffect(() => {
    void fetchProjects(debouncedProjectSearchTerm);
  }, [debouncedProjectSearchTerm, fetchProjects]);

  useEffect(() => {
    void fetchSuppliers(debouncedSupplierSearchTerm);
  }, [debouncedSupplierSearchTerm, fetchSuppliers]);

  useEffect(() => {
    setProjectsPage(1);
  }, [debouncedProjectSearchTerm]);

  useEffect(() => {
    setSuppliersPage(1);
  }, [debouncedSupplierSearchTerm]);

  useEffect(() => {
    setSubprojectsPage(1);
  }, [selectedProjectId]);

  useEffect(() => {
    void refreshCategories({
      active: categoryStatusFilter === 'active' ? true : undefined,
      limit: 200,
      search: debouncedCategorySearchTerm.trim() || undefined,
    })
      .catch(() => undefined)
      .finally(() => {
        setCategoriesHydrated(true);
      });
  }, [debouncedCategorySearchTerm, categoryStatusFilter, refreshCategories]);

  useEffect(() => {
    void refreshShippingLocations({
      active: shippingStatusFilter === 'active' ? true : undefined,
      limit: 200,
      search: debouncedShippingSearchTerm.trim() || undefined,
    })
      .catch(() => undefined)
      .finally(() => {
        setShippingHydrated(true);
      });
  }, [debouncedShippingSearchTerm, shippingStatusFilter, refreshShippingLocations]);

  const categoryRows = useMemo(
    () =>
      categories
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [categories],
  );
  const shippingRows = useMemo(
    () =>
      shippingLocations
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [shippingLocations],
  );
  const selectedProjectName = useMemo(
    () => projects.find((project) => project.id === selectedProjectId)?.name ?? '',
    [projects, selectedProjectId],
  );
  const paginatedProjects = useMemo(
    () =>
      projects.slice(
        (projectsPage - 1) * MAINTENANCE_PAGE_SIZE,
        projectsPage * MAINTENANCE_PAGE_SIZE,
      ),
    [projects, projectsPage],
  );
  const paginatedSubprojects = useMemo(
    () =>
      subprojects.slice(
        (subprojectsPage - 1) * MAINTENANCE_PAGE_SIZE,
        subprojectsPage * MAINTENANCE_PAGE_SIZE,
      ),
    [subprojects, subprojectsPage],
  );
  const paginatedSuppliers = useMemo(
    () =>
      suppliers.slice(
        (suppliersPage - 1) * MAINTENANCE_PAGE_SIZE,
        suppliersPage * MAINTENANCE_PAGE_SIZE,
      ),
    [suppliers, suppliersPage],
  );
  const totalProjectPages = Math.max(1, Math.ceil(projects.length / MAINTENANCE_PAGE_SIZE));
  const totalSubprojectPages = Math.max(1, Math.ceil(subprojects.length / MAINTENANCE_PAGE_SIZE));
  const totalSupplierPages = Math.max(1, Math.ceil(suppliers.length / MAINTENANCE_PAGE_SIZE));

  useEffect(() => {
    setProjectsPage((previous) => Math.min(previous, totalProjectPages));
  }, [totalProjectPages]);

  useEffect(() => {
    setSubprojectsPage((previous) => Math.min(previous, totalSubprojectPages));
  }, [totalSubprojectPages]);

  useEffect(() => {
    setSuppliersPage((previous) => Math.min(previous, totalSupplierPages));
  }, [totalSupplierPages]);

  const handleCompanyInputChange = (field: keyof CompanySettingsForm, value: string) => {
    setCompanyForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleFinanceInputChange = (field: keyof FinanceSettingsForm, value: string) => {
    setFinanceForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleFinanceCurrencyChange = (currencyCode: string) => {
    const selectedCurrency = CURRENCY_OPTIONS.find((option) => option.code === currencyCode);
    setFinanceForm((previous) => ({
      ...previous,
      currencyCode,
      currencyLocale: selectedCurrency?.locale ?? previous.currencyLocale,
    }));
  };

  const handleAlertRuleChange = (
    field: keyof AlertSettingsForm,
    key: keyof AlertRuleSettings,
    value: string | boolean,
  ) => {
    setAlertForm((previous) => ({
      ...previous,
      [field]: {
        ...previous[field],
        [key]: value,
      },
    }));
  };

  const handleSaveCompany = async () => {
    setCompanySaving(true);
    try {
      const response = await apiClient.put<SettingsApiResponse>('/settings/company', {
        company_name: companyForm.companyName.trim(),
        tax_id: companyForm.taxId.trim(),
        address: companyForm.address.trim(),
        phone: companyForm.phone.trim(),
        email: companyForm.email.trim(),
        website: companyForm.website.trim(),
        mercantile_registry: companyForm.mercantileRegistry.trim(),
      });
      applySettingsToForms(response.data);
      void refreshSettings();
      toast.success('Configuración de empresa guardada correctamente.');
    } catch (error) {
      toast.error(normalizeApiError(error, 'No pudimos guardar la configuración de empresa.'));
    } finally {
      setCompanySaving(false);
    }
  };

  const handleSaveFinance = async () => {
    const parsedIva = Number(financeForm.ivaRatePct);
    if (!Number.isFinite(parsedIva)) {
      toast.error('Introduce un IVA válido.');
      return;
    }

    setFinanceSaving(true);
    try {
      const response = await apiClient.put<SettingsApiResponse>('/settings/finances', {
        currency_code: financeForm.currencyCode,
        currency_locale: financeForm.currencyLocale.trim(),
        iva_rate_pct: parsedIva,
      });
      applySettingsToForms(response.data);
      void refreshSettings();
      toast.success(
        'IVA actualizado. Se recalculan los importes con IVA en finanzas, proyectos, subproyectos activos y exportaciones.',
      );
    } catch (error) {
      toast.error(normalizeApiError(error, 'No pudimos guardar la configuración de finanzas.'));
    } finally {
      setFinanceSaving(false);
    }
  };

  const handleRequestSaveFinance = () => {
    const nextIva = Number.parseFloat(financeForm.ivaRatePct);
    const previousIva = Number.parseFloat(loadedIvaRatePct);
    const ivaChanged =
      Number.isFinite(nextIva) &&
      Number.isFinite(previousIva) &&
      Math.abs(nextIva - previousIva) > 0.0001;

    if (ivaChanged) {
      setFinanceWarningOpen(true);
      return;
    }
    void handleSaveFinance();
  };

  const handleSaveAlerts = async () => {
    const orderEmailDays = Number.parseInt(alertForm.orderEmailOverdue.thresholdValue, 10);
    const orderCallDays = Number.parseInt(alertForm.orderCallOverdue.thresholdValue, 10);
    const orderConfirmDays = Number.parseInt(alertForm.orderConfirmOverdue.thresholdValue, 10);
    const subprojectDays = Number.parseInt(
      alertForm.subprojectPendingItemsOverdue.thresholdValue,
      10,
    );
    const subprojectCostDeviationPct = Number.parseFloat(
      alertForm.subprojectCostDeviationWatch.thresholdValue,
    );
    const subprojectPendingOrdersLimit = Number.parseInt(
      alertForm.subprojectPendingOrdersWatch.thresholdValue,
      10,
    );

    if (
      [orderEmailDays, orderCallDays, orderConfirmDays, subprojectDays, subprojectPendingOrdersLimit].some(Number.isNaN) ||
      !Number.isFinite(subprojectCostDeviationPct)
    ) {
      toast.error('Todos los umbrales de alertas deben ser números válidos.');
      return;
    }

    setAlertsSaving(true);
    try {
      const response = await apiClient.put<SettingsApiResponse>('/settings/alerts', {
        order_email_overdue_enabled: alertForm.orderEmailOverdue.enabled,
        order_email_overdue_days: orderEmailDays,
        order_call_overdue_enabled: alertForm.orderCallOverdue.enabled,
        order_call_overdue_days: orderCallDays,
        order_confirm_overdue_enabled: alertForm.orderConfirmOverdue.enabled,
        order_confirm_overdue_days: orderConfirmDays,
        subproject_pending_items_overdue_enabled: alertForm.subprojectPendingItemsOverdue.enabled,
        subproject_pending_items_overdue_days: subprojectDays,
        subproject_cost_deviation_watch_enabled: alertForm.subprojectCostDeviationWatch.enabled,
        subproject_cost_deviation_watch_pct: subprojectCostDeviationPct,
        subproject_pending_orders_watch_enabled: alertForm.subprojectPendingOrdersWatch.enabled,
        subproject_pending_orders_watch_limit: subprojectPendingOrdersLimit,
      });
      applySettingsToForms(response.data);
      void refreshSettings();
      toast.success('Umbrales de alertas guardados correctamente.');
    } catch (error) {
      toast.error(normalizeApiError(error, 'No pudimos guardar la configuración de alertas.'));
    } finally {
      setAlertsSaving(false);
    }
  };

  const openCreateUserDialog = () => {
    setUserDialogMode('create');
    setEditingUser(null);
    setUserNameDraft('');
    setUserApellidosDraft('');
    setUserEmailDraft('');
    setUserPasswordDraft('');
    setUserRoleDraft('trabajador');
    setUserActiveDraft(true);
    setIsUserDialogOpen(true);
  };

  const openEditUserDialog = (user: SettingsUserRow) => {
    setUserDialogMode('edit');
    setEditingUser(user);
    setUserNameDraft(user.name);
    setUserApellidosDraft(user.apellidos);
    setUserEmailDraft(user.email);
    setUserPasswordDraft('');
    setUserRoleDraft(user.role);
    setUserActiveDraft(user.active);
    setIsUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    const name = userNameDraft.trim();
    const apellidos = userApellidosDraft.trim();
    if (!name || !apellidos) {
      toast.error('Nombre y apellidos son obligatorios.');
      return;
    }

    setUserSaving(true);
    try {
      if (userDialogMode === 'create') {
        const email = userEmailDraft.trim().toLowerCase();
        if (!email) {
          toast.error('El email es obligatorio.');
          return;
        }
        if (userPasswordDraft.length < 8) {
          toast.error('La contraseña debe tener al menos 8 caracteres.');
          return;
        }
        const createResponse = await apiClient.post<SettingsUserRow>('/users/create', {
          name,
          apellidos,
          email,
          password: userPasswordDraft,
          role: userRoleDraft,
        });
        if (!userActiveDraft) {
          await apiClient.put<SettingsUserRow>(`/users/${createResponse.data.id}`, { active: false });
        }
        toast.success('Usuario creado correctamente.');
      } else {
        if (!editingUser) return;
        await apiClient.put<SettingsUserRow>(`/users/${editingUser.id}`, {
          name,
          apellidos,
          role: userRoleDraft,
          active: userActiveDraft,
        });
        toast.success('Usuario actualizado correctamente.');
      }
      setIsUserDialogOpen(false);
      setEditingUser(null);
      setUserPasswordDraft('');
      await fetchUsers();
    } catch (error) {
      toast.error(
        normalizeApiError(
          error,
          userDialogMode === 'create'
            ? 'No pudimos crear el usuario.'
            : 'No pudimos actualizar el usuario.',
        ),
      );
    } finally {
      setUserSaving(false);
    }
  };

  const openCreateCategoryDialog = () => {
    setCategoryDialogMode('create');
    setCategoryDialogId(null);
    setCategoryDialogName('');
    setCategoryDialogActive(true);
    setCategoryDialogOpen(true);
  };

  const openEditCategoryDialog = (id: string, name: string, active: boolean) => {
    setCategoryDialogMode('edit');
    setCategoryDialogId(id);
    setCategoryDialogName(name);
    setCategoryDialogActive(active);
    setCategoryDialogOpen(true);
  };

  const openCreateShippingDialog = () => {
    setShippingDialogMode('create');
    setShippingDialogId(null);
    setShippingDialogName('');
    setShippingDialogActive(true);
    setShippingDialogOpen(true);
  };

  const openEditShippingDialog = (id: string, name: string, active: boolean) => {
    setShippingDialogMode('edit');
    setShippingDialogId(id);
    setShippingDialogName(name);
    setShippingDialogActive(active);
    setShippingDialogOpen(true);
  };

  const handleSubmitCategory = async () => {
    const trimmedName = categoryDialogName.trim();
    if (!trimmedName) {
      toast.error('El nombre de la categoría es obligatorio.');
      return;
    }

    setCategorySubmitting(true);
    try {
      if (categoryDialogMode === 'edit' && categoryDialogId) {
        await updateCategory(categoryDialogId, {
          name: trimmedName,
          active: categoryDialogActive,
        });
        toast.success('Categoría actualizada correctamente.');
      } else {
        await createCategory({ name: trimmedName, active: categoryDialogActive });
        toast.success('Categoría creada correctamente.');
      }
      await refreshCategories({
        active: categoryStatusFilter === 'active' ? true : undefined,
        limit: 200,
        search: categorySearchTerm.trim() || undefined,
      });
      setCategoryDialogOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No pudimos guardar la categoría.');
    } finally {
      setCategorySubmitting(false);
    }
  };

  const handleSubmitShipping = async () => {
    const trimmedName = shippingDialogName.trim();
    if (!trimmedName) {
      toast.error('El nombre del lugar de envío es obligatorio.');
      return;
    }

    setShippingSubmitting(true);
    try {
      if (shippingDialogMode === 'edit' && shippingDialogId) {
        await updateShippingLocation(shippingDialogId, {
          name: trimmedName,
          active: shippingDialogActive,
        });
        toast.success('Lugar de envío actualizado correctamente.');
      } else {
        await createShippingLocation({ name: trimmedName, active: shippingDialogActive });
        toast.success('Lugar de envío creado correctamente.');
      }
      await refreshShippingLocations({
        active: shippingStatusFilter === 'active' ? true : undefined,
        limit: 200,
        search: shippingSearchTerm.trim() || undefined,
      });
      setShippingDialogOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'No pudimos guardar el lugar de envío.',
      );
    } finally {
      setShippingSubmitting(false);
    }
  };

  const openDeleteDialog = (kind: MaintenanceDeleteTarget['kind'], id: string, label: string) => {
    setPendingDelete({ kind, id, label });
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteSubmitting(true);
    try {
      if (pendingDelete.kind === 'category') {
        await deleteCategory(pendingDelete.id);
        await refreshCategories({
          active: categoryStatusFilter === 'active' ? true : undefined,
          limit: 200,
          search: categorySearchTerm.trim() || undefined,
        });
        toast.success('Categoría eliminada correctamente.');
        setPendingDelete(null);
        return;
      }

      if (pendingDelete.kind === 'shippingLocation') {
        await deleteShippingLocation(pendingDelete.id);
        await refreshShippingLocations({
          active: shippingStatusFilter === 'active' ? true : undefined,
          limit: 200,
          search: shippingSearchTerm.trim() || undefined,
        });
        toast.success('Lugar de envío eliminado correctamente.');
        setPendingDelete(null);
        return;
      }

      if (pendingDelete.kind === 'project') {
        await apiClient.delete(`/projects/${pendingDelete.id}`);
        await fetchProjects(projectSearchTerm);
        toast.success('Proyecto eliminado correctamente.');
      } else if (pendingDelete.kind === 'subproject') {
        await apiClient.delete(`/subprojects/${pendingDelete.id}`);
        if (selectedProjectId) {
          await fetchSubprojects(selectedProjectId);
        }
        toast.success('Subproyecto eliminado correctamente.');
      } else if (pendingDelete.kind === 'supplier') {
        await apiClient.delete(`/suppliers/${pendingDelete.id}`);
        await fetchSuppliers(supplierSearchTerm);
        toast.success('Proveedor eliminado correctamente.');
      }
      setPendingDelete(null);
    } catch (error) {
      toast.error(normalizeApiError(error, 'No pudimos completar el borrado.'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const deleteDialogDescription = useMemo(() => {
    if (!pendingDelete) return '';
    if (pendingDelete.kind === 'project') {
      return `Vas a eliminar el proyecto "${pendingDelete.label}". Se eliminarán también sus subproyectos, pedidos y datos financieros asociados. Esta acción no se puede deshacer.`;
    }
    if (pendingDelete.kind === 'subproject') {
      return `Vas a eliminar el subproyecto "${pendingDelete.label}". Esto impactará en finanzas porque se eliminarán sus pedidos, estancias, items y extras asociados. Esta acción no se puede deshacer.`;
    }
    if (pendingDelete.kind === 'supplier') {
      return `Vas a eliminar el proveedor "${pendingDelete.label}". Se eliminarán sus items del catálogo y los pedidos/items existentes quedarán asociados al proveedor técnico del sistema para mantener histórico. Las copias de items ya creadas dentro de subproyectos se conservan. Esta acción no se puede deshacer.`;
    }
    if (pendingDelete.kind === 'category') {
      return `Vas a eliminar la categoría "${pendingDelete.label}". Esta acción no se puede deshacer.`;
    }
    return `Vas a eliminar el lugar de envío "${pendingDelete.label}". Esta acción no se puede deshacer.`;
  }, [pendingDelete]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl text-gray-900 mb-2">Configuración</h1>
        <p className="text-gray-600">Ajustes globales del estudio y administración avanzada</p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as SettingsTabKey)}
        className="space-y-4"
      >
        <TabsList className="grid w-full grid-cols-2 gap-1 md:grid-cols-6">
          <TabsTrigger value="company">Empresa</TabsTrigger>
          <TabsTrigger value="finances">Finanzas</TabsTrigger>
          <TabsTrigger value="alerts">Alertas</TabsTrigger>
          <TabsTrigger value="masters">Maestros</TabsTrigger>
          <TabsTrigger value="users">Usuarios</TabsTrigger>
          <TabsTrigger value="maintenance">Mantenimiento</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="space-y-6">
          {!settingsHydrated ? (
            <Card className="shadow-sm">
              <CardContent className="py-10">
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando configuración de empresa...
                </div>
              </CardContent>
            </Card>
          ) : (
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Información de Empresa</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company-name">Nombre de la empresa</Label>
                  <Input
                    id="company-name"
                    className="mt-1"
                    value={companyForm.companyName}
                    onChange={(event) => handleCompanyInputChange('companyName', event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="company-tax-id">CIF/NIF</Label>
                  <Input
                    id="company-tax-id"
                    className="mt-1"
                    value={companyForm.taxId}
                    onChange={(event) => handleCompanyInputChange('taxId', event.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="company-address">Dirección</Label>
                  <Input
                    id="company-address"
                    className="mt-1"
                    value={companyForm.address}
                    onChange={(event) => handleCompanyInputChange('address', event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="company-phone">Teléfono</Label>
                  <Input
                    id="company-phone"
                    className="mt-1"
                    value={companyForm.phone}
                    onChange={(event) => handleCompanyInputChange('phone', event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="company-email">Email</Label>
                  <Input
                    id="company-email"
                    className="mt-1"
                    value={companyForm.email}
                    onChange={(event) => handleCompanyInputChange('email', event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="company-website">Sitio web</Label>
                  <Input
                    id="company-website"
                    className="mt-1"
                    value={companyForm.website}
                    onChange={(event) => handleCompanyInputChange('website', event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="company-registry">Registro Mercantil</Label>
                  <Input
                    id="company-registry"
                    className="mt-1"
                    value={companyForm.mercantileRegistry}
                    onChange={(event) =>
                      handleCompanyInputChange('mercantileRegistry', event.target.value)
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    void handleSaveCompany();
                  }}
                  disabled={companySaving || settingsLoading}
                >
                  {companySaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar empresa'}
                </Button>
              </div>
            </CardContent>
          </Card>
          )}
        </TabsContent>

        <TabsContent value="finances" className="space-y-6">
          {!settingsHydrated ? (
            <Card className="shadow-sm">
              <CardContent className="py-10">
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando configuración financiera...
                </div>
              </CardContent>
            </Card>
          ) : (
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Configuración de Finanzas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="finance-currency-code">Moneda</Label>
                  <Select
                    value={financeForm.currencyCode}
                    onValueChange={handleFinanceCurrencyChange}
                  >
                    <SelectTrigger id="finance-currency-code" className="mt-1">
                      <SelectValue placeholder="Selecciona moneda" />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((option) => (
                        <SelectItem key={option.code} value={option.code}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="finance-currency-locale">Locale de moneda</Label>
                  <Input
                    id="finance-currency-locale"
                    className="mt-1"
                    value={financeForm.currencyLocale}
                    readOnly
                  />
                </div>
                <div>
                  <Label htmlFor="finance-iva">IVA (%)</Label>
                  <Input
                    id="finance-iva"
                    type="number"
                    min="0"
                    step="0.01"
                    className="mt-1"
                    value={financeForm.ivaRatePct}
                    onChange={(event) => handleFinanceInputChange('ivaRatePct', event.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    handleRequestSaveFinance();
                  }}
                  disabled={financeSaving || settingsLoading}
                >
                  {financeSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Guardar finanzas'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          )}
        </TabsContent>

        <TabsContent value="alerts" className="space-y-6">
          {!settingsHydrated ? (
            <Card className="shadow-sm">
              <CardContent className="py-10">
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando configuración de alertas...
                </div>
              </CardContent>
            </Card>
          ) : (
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Umbrales de Alertas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4">
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <p className="text-gray-900">Pedido sin email enviado</p>
                    <p className="text-sm text-gray-600">
                      Alerta cuando no se ha enviado email al proveedor.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="1"
                      className="w-20"
                      value={alertForm.orderEmailOverdue.thresholdValue}
                      onChange={(event) =>
                        handleAlertRuleChange('orderEmailOverdue', 'thresholdValue', event.target.value)
                      }
                    />
                    <span className="text-sm text-gray-600">días</span>
                    <Switch
                      checked={alertForm.orderEmailOverdue.enabled}
                      onCheckedChange={(checked) =>
                        handleAlertRuleChange('orderEmailOverdue', 'enabled', checked)
                      }
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <p className="text-gray-900">Pedido sin llamada</p>
                    <p className="text-sm text-gray-600">
                      Alerta cuando no se registra llamada tras el email.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="1"
                      className="w-20"
                      value={alertForm.orderCallOverdue.thresholdValue}
                      onChange={(event) =>
                        handleAlertRuleChange('orderCallOverdue', 'thresholdValue', event.target.value)
                      }
                    />
                    <span className="text-sm text-gray-600">días</span>
                    <Switch
                      checked={alertForm.orderCallOverdue.enabled}
                      onCheckedChange={(checked) =>
                        handleAlertRuleChange('orderCallOverdue', 'enabled', checked)
                      }
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <p className="text-gray-900">Pedido sin confirmación</p>
                    <p className="text-sm text-gray-600">
                      Alerta cuando el proveedor sigue sin confirmar el pedido.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="1"
                      className="w-20"
                      value={alertForm.orderConfirmOverdue.thresholdValue}
                      onChange={(event) =>
                        handleAlertRuleChange('orderConfirmOverdue', 'thresholdValue', event.target.value)
                      }
                    />
                    <span className="text-sm text-gray-600">días</span>
                    <Switch
                      checked={alertForm.orderConfirmOverdue.enabled}
                      onCheckedChange={(checked) =>
                        handleAlertRuleChange('orderConfirmOverdue', 'enabled', checked)
                      }
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <p className="text-gray-900">Subproyecto con items pendientes</p>
                    <p className="text-sm text-gray-600">Alerta de pendientes tras primer pedido.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="1"
                      className="w-20"
                      value={alertForm.subprojectPendingItemsOverdue.thresholdValue}
                      onChange={(event) =>
                        handleAlertRuleChange(
                          'subprojectPendingItemsOverdue',
                          'thresholdValue',
                          event.target.value,
                        )
                      }
                    />
                    <span className="text-sm text-gray-600">días</span>
                    <Switch
                      checked={alertForm.subprojectPendingItemsOverdue.enabled}
                      onCheckedChange={(checked) =>
                        handleAlertRuleChange('subprojectPendingItemsOverdue', 'enabled', checked)
                      }
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <p className="text-gray-900">Subproyecto con sobrecoste</p>
                    <p className="text-sm text-gray-600">
                      Vigila desvíos de coste altos respecto al coste real total del subproyecto.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-24"
                      value={alertForm.subprojectCostDeviationWatch.thresholdValue}
                      onChange={(event) =>
                        handleAlertRuleChange(
                          'subprojectCostDeviationWatch',
                          'thresholdValue',
                          event.target.value,
                        )
                      }
                    />
                    <span className="text-sm text-gray-600">%</span>
                    <Switch
                      checked={alertForm.subprojectCostDeviationWatch.enabled}
                      onCheckedChange={(checked) =>
                        handleAlertRuleChange('subprojectCostDeviationWatch', 'enabled', checked)
                      }
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <p className="text-gray-900">Subproyecto con demasiados pedidos pendientes</p>
                    <p className="text-sm text-gray-600">
                      Vigila subproyectos con demasiados pedidos abiertos en paralelo.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="0"
                      className="w-20"
                      value={alertForm.subprojectPendingOrdersWatch.thresholdValue}
                      onChange={(event) =>
                        handleAlertRuleChange(
                          'subprojectPendingOrdersWatch',
                          'thresholdValue',
                          event.target.value,
                        )
                      }
                    />
                    <span className="text-sm text-gray-600">pedidos</span>
                    <Switch
                      checked={alertForm.subprojectPendingOrdersWatch.enabled}
                      onCheckedChange={(checked) =>
                        handleAlertRuleChange('subprojectPendingOrdersWatch', 'enabled', checked)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    void handleSaveAlerts();
                  }}
                  disabled={alertsSaving || settingsLoading}
                >
                  {alertsSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar alertas'}
                </Button>
              </div>
            </CardContent>
          </Card>
          )}
        </TabsContent>

        <TabsContent value="masters" className="space-y-6">
          {!mastersHydrated ? (
            <Card className="shadow-sm">
              <CardContent className="py-10">
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando maestros...
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Maestros · Categorías</CardTitle>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={openCreateCategoryDialog}>
                <Plus className="mr-2 h-4 w-4" />
                Nueva categoría
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <Input
                  placeholder="Buscar categoría por nombre"
                  value={categorySearchTerm}
                  onChange={(event) => setCategorySearchTerm(event.target.value)}
                />
                <Select
                  value={categoryStatusFilter}
                  onValueChange={(value) => setCategoryStatusFilter(value as MasterStatusFilter)}
                >
                  <SelectTrigger className="w-full md:w-44">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Solo activas</SelectItem>
                    <SelectItem value="all">Mostrar todas</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  onClick={() => {
                    void refreshCategories({
                      active: categoryStatusFilter === 'active' ? true : undefined,
                      limit: 200,
                      search: categorySearchTerm.trim() || undefined,
                    }).catch(() => undefined);
                  }}
                  disabled={categoriesLoading}
                >
                  {categoriesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Recargar'}
                </Button>
              </div>

              {categoriesLoading ? (
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando categorías...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead className="text-center">Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-gray-500 py-6">
                            No hay categorías disponibles.
                          </TableCell>
                        </TableRow>
                      ) : (
                        categoryRows.map((category) => (
                          <TableRow key={category.id}>
                            <TableCell>{category.name}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant={category.active ? 'finished' : 'secondary'}>
                                {category.active ? 'Activa' : 'Inactiva'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    openEditCategoryDialog(
                                      category.id,
                                      category.name,
                                      category.active,
                                    )
                                  }
                                >
                                  <Pencil className="mr-1 h-4 w-4" />
                                  Editar
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700"
                                  onClick={() =>
                                    openDeleteDialog('category', category.id, category.name)
                                  }
                                >
                                  <Trash2 className="mr-1 h-4 w-4" />
                                  Eliminar
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Maestros · Lugares de Envío</CardTitle>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={openCreateShippingDialog}>
                <Plus className="mr-2 h-4 w-4" />
                Nuevo lugar
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <Input
                  placeholder="Buscar lugar de envío por nombre"
                  value={shippingSearchTerm}
                  onChange={(event) => setShippingSearchTerm(event.target.value)}
                />
                <Select
                  value={shippingStatusFilter}
                  onValueChange={(value) => setShippingStatusFilter(value as MasterStatusFilter)}
                >
                  <SelectTrigger className="w-full md:w-44">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Solo activos</SelectItem>
                    <SelectItem value="all">Mostrar todos</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  onClick={() => {
                    void refreshShippingLocations({
                      active: shippingStatusFilter === 'active' ? true : undefined,
                      limit: 200,
                      search: shippingSearchTerm.trim() || undefined,
                    }).catch(() => undefined);
                  }}
                  disabled={shippingLocationsLoading}
                >
                  {shippingLocationsLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Recargar'
                  )}
                </Button>
              </div>

              {shippingLocationsLoading ? (
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando lugares de envío...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead className="text-center">Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shippingRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-gray-500 py-6">
                            No hay lugares de envío disponibles.
                          </TableCell>
                        </TableRow>
                      ) : (
                        shippingRows.map((location) => (
                          <TableRow key={location.id}>
                            <TableCell>{location.name}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant={location.active ? 'finished' : 'secondary'}>
                                {location.active ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    openEditShippingDialog(
                                      location.id,
                                      location.name,
                                      location.active,
                                    )
                                  }
                                >
                                  <Pencil className="mr-1 h-4 w-4" />
                                  Editar
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700"
                                  onClick={() =>
                                    openDeleteDialog(
                                      'shippingLocation',
                                      location.id,
                                      location.name,
                                    )
                                  }
                                >
                                  <Trash2 className="mr-1 h-4 w-4" />
                                  Eliminar
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Gestión de Usuarios</CardTitle>
              <div className="flex items-center gap-2">
                <Select
                  value={usersStatusFilter}
                  onValueChange={(value) => setUsersStatusFilter(value as UserStatusFilter)}
                >
                  <SelectTrigger className="w-44">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Solo activos</SelectItem>
                    <SelectItem value="all">Todos</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void fetchUsers();
                  }}
                  disabled={usersLoading}
                >
                  {usersLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Recargar'}
                </Button>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={openCreateUserDialog}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Añadir usuario
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {usersError ? <p className="text-sm text-red-600 mb-4">{usersError}</p> : null}

              {usersLoading ? (
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando usuarios...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="text-center">Rol</TableHead>
                        <TableHead className="text-center">Estado</TableHead>
                        <TableHead className="text-center">Alta</TableHead>
                        <TableHead className="text-center">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-gray-500 py-6">
                            No hay usuarios para mostrar.
                          </TableCell>
                        </TableRow>
                      ) : (
                        users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{`${user.name} ${user.apellidos}`}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell className="text-center">
                              {ROLE_LABELS[user.role] ?? user.role}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={user.active ? 'finished' : 'secondary'}>
                                {user.active ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {new Date(user.created_at).toLocaleDateString('es-ES')}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditUserDialog(user)}
                              >
                                Editar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="maintenance" className="space-y-6">
          <Card className="border-red-200 bg-red-50/40 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <ShieldAlert className="h-5 w-5" />
                Zona de Mantenimiento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-red-700">
                Estas acciones son destructivas y ejecutan borrado real. Confirma siempre antes de
                eliminar.
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Borrado de Proyectos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <Input
                  placeholder="Buscar proyecto por nombre o cliente"
                  value={projectSearchTerm}
                  onChange={(event) => setProjectSearchTerm(event.target.value)}
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    void fetchProjects(projectSearchTerm);
                  }}
                  disabled={projectsLoading}
                >
                  {projectsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Recargar'}
                </Button>
              </div>

              {projectsError ? <p className="text-sm text-red-600">{projectsError}</p> : null}

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ref</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-center">Estado</TableHead>
                      <TableHead className="text-center">Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projects.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-6">
                          No hay proyectos para mantenimiento.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedProjects.map((project) => (
                        <TableRow key={project.id}>
                          <TableCell>{project.ref}</TableCell>
                          <TableCell>{project.name}</TableCell>
                          <TableCell>{project.client}</TableCell>
                          <TableCell className="text-center">{formatStatusLabel(project.status)}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              className="bg-red-600 hover:bg-red-700"
                              onClick={() => openDeleteDialog('project', project.id, project.name)}
                            >
                              Eliminar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {projects.length > 0 ? (
                <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setProjectsPage((previous) => Math.max(1, previous - 1))}
                    disabled={projectsPage <= 1}
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-gray-600">
                    Página {projectsPage} de {totalProjectPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setProjectsPage((previous) => Math.min(totalProjectPages, previous + 1))
                    }
                    disabled={projectsPage >= totalProjectPages}
                  >
                    Siguiente
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Borrado de Subproyectos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <Label htmlFor="maintenance-project-select" className="md:min-w-48">
                  Proyecto fuente
                </Label>
                <select
                  id="maintenance-project-select"
                  className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
                  value={selectedProjectId}
                  onChange={(event) => setSelectedProjectId(event.target.value)}
                >
                  <option value="">Selecciona un proyecto</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.ref} · {project.name}
                    </option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!selectedProjectId) return;
                    void fetchSubprojects(selectedProjectId);
                  }}
                  disabled={subprojectsLoading || !selectedProjectId}
                >
                  {subprojectsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Recargar'}
                </Button>
              </div>

              {selectedProjectName ? (
                <p className="text-sm text-gray-600">Subproyectos de: {selectedProjectName}</p>
              ) : null}
              {subprojectsError ? <p className="text-sm text-red-600">{subprojectsError}</p> : null}

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ref</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-center">Estado</TableHead>
                      <TableHead className="text-center">Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subprojects.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-6">
                          No hay subproyectos para mantenimiento.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedSubprojects.map((subproject) => (
                        <TableRow key={subproject.id}>
                          <TableCell>{subproject.ref ?? '—'}</TableCell>
                          <TableCell>{subproject.name}</TableCell>
                          <TableCell>{subproject.type}</TableCell>
                          <TableCell className="text-center">
                            {formatStatusLabel(subproject.status)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              className="bg-red-600 hover:bg-red-700"
                              onClick={() =>
                                openDeleteDialog('subproject', subproject.id, subproject.name)
                              }
                            >
                              Eliminar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {subprojects.length > 0 ? (
                <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSubprojectsPage((previous) => Math.max(1, previous - 1))}
                    disabled={subprojectsPage <= 1}
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-gray-600">
                    Página {subprojectsPage} de {totalSubprojectPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setSubprojectsPage((previous) =>
                        Math.min(totalSubprojectPages, previous + 1),
                      )
                    }
                    disabled={subprojectsPage >= totalSubprojectPages}
                  >
                    Siguiente
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Borrado de Proveedores</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <Input
                  placeholder="Buscar proveedor por nombre o email"
                  value={supplierSearchTerm}
                  onChange={(event) => setSupplierSearchTerm(event.target.value)}
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    void fetchSuppliers(supplierSearchTerm);
                  }}
                  disabled={suppliersLoading}
                >
                  {suppliersLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Recargar'}
                </Button>
              </div>

              {suppliersError ? <p className="text-sm text-red-600">{suppliersError}</p> : null}

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Categorías</TableHead>
                      <TableHead className="text-center">Estado</TableHead>
                      <TableHead className="text-center">Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppliers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-6">
                          No hay proveedores para mantenimiento.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedSuppliers.map((supplier) => (
                        <TableRow key={supplier.id}>
                          <TableCell>{supplier.name}</TableCell>
                          <TableCell>{supplier.email ?? '—'}</TableCell>
                          <TableCell>
                            <SupplierCategoryBadges categories={supplier.categories} />
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={supplier.active ? 'finished' : 'secondary'}>
                              {supplier.active ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              className="bg-red-600 hover:bg-red-700"
                              onClick={() => openDeleteDialog('supplier', supplier.id, supplier.name)}
                            >
                              Eliminar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {suppliers.length > 0 ? (
                <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSuppliersPage((previous) => Math.max(1, previous - 1))}
                    disabled={suppliersPage <= 1}
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-gray-600">
                    Página {suppliersPage} de {totalSupplierPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setSuppliersPage((previous) => Math.min(totalSupplierPages, previous + 1))
                    }
                    disabled={suppliersPage >= totalSupplierPages}
                  >
                    Siguiente
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog open={financeWarningOpen} onOpenChange={setFinanceWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar actualización del IVA?</AlertDialogTitle>
            <AlertDialogDescription>
              Si guardas este cambio, el nuevo IVA se aplicará en finanzas, resumen de proyecto,
              subproyectos activos y exportaciones. Los proyectos en estado finalizado conservarán
              su IVA congelado y no se recalcularán con este cambio.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={financeSaving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                setFinanceWarningOpen(false);
                void handleSaveFinance();
              }}
              disabled={financeSaving}
            >
              Confirmar y guardar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
            <AlertDialogDescription>{deleteDialogDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(event) => {
                event.preventDefault();
                void handleConfirmDelete();
              }}
              disabled={deleteSubmitting}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={isUserDialogOpen}
        onOpenChange={(open) => {
          setIsUserDialogOpen(open);
          if (!open) {
            setEditingUser(null);
            setUserPasswordDraft('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {userDialogMode === 'create' ? 'Nuevo usuario' : 'Editar usuario'}
            </DialogTitle>
            <DialogDescription>
              {userDialogMode === 'create'
                ? 'Completa los datos para crear un usuario nuevo.'
                : 'Actualiza datos, rol y estado del usuario.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="user-edit-name">Nombre</Label>
              <Input
                id="user-edit-name"
                className="mt-1"
                value={userNameDraft}
                onChange={(event) => setUserNameDraft(event.target.value)}
                disabled={userSaving}
              />
            </div>
            <div>
              <Label htmlFor="user-edit-apellidos">Apellidos</Label>
              <Input
                id="user-edit-apellidos"
                className="mt-1"
                value={userApellidosDraft}
                onChange={(event) => setUserApellidosDraft(event.target.value)}
                disabled={userSaving}
              />
            </div>
            <div>
              <Label htmlFor="user-edit-email">Email</Label>
              <Input
                id="user-edit-email"
                type="email"
                className="mt-1"
                value={userEmailDraft}
                onChange={(event) => setUserEmailDraft(event.target.value)}
                disabled={userSaving || userDialogMode === 'edit'}
              />
            </div>
            {userDialogMode === 'create' ? (
              <div>
                <Label htmlFor="user-edit-password">Contraseña</Label>
                <Input
                  id="user-edit-password"
                  type="password"
                  className="mt-1"
                  value={userPasswordDraft}
                  onChange={(event) => setUserPasswordDraft(event.target.value)}
                  disabled={userSaving}
                />
              </div>
            ) : null}
            <div>
              <Label htmlFor="user-edit-role">Rol</Label>
              <Select value={userRoleDraft} onValueChange={(value) => setUserRoleDraft(value as UserRole)}>
                <SelectTrigger id="user-edit-role" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="trabajador">Trabajador</SelectItem>
                  <SelectItem value="finanza">Finanzas</SelectItem>
                  <SelectItem value="logistica">Logística</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <p className="text-sm text-gray-700">Activo</p>
              <Switch
                checked={userActiveDraft}
                onCheckedChange={setUserActiveDraft}
                disabled={userSaving}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsUserDialogOpen(false);
                setEditingUser(null);
              }}
              disabled={userSaving}
            >
              Cancelar
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                void handleSaveUser();
              }}
              disabled={userSaving}
            >
              {userSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : userDialogMode === 'create' ? (
                'Crear usuario'
              ) : (
                'Guardar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {categoryDialogMode === 'create' ? 'Nueva categoría' : 'Editar categoría'}
            </DialogTitle>
            <DialogDescription>
              Define el nombre y el estado de la categoría para el maestro global.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="category-dialog-name">Nombre</Label>
              <Input
                id="category-dialog-name"
                className="mt-1"
                value={categoryDialogName}
                onChange={(event) => setCategoryDialogName(event.target.value)}
                disabled={categorySubmitting}
              />
            </div>
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <p className="text-sm text-gray-700">Activa</p>
              <Switch
                checked={categoryDialogActive}
                onCheckedChange={setCategoryDialogActive}
                disabled={categorySubmitting}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCategoryDialogOpen(false)}
              disabled={categorySubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                void handleSubmitCategory();
              }}
              disabled={categorySubmitting}
            >
              {categorySubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shippingDialogOpen} onOpenChange={setShippingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {shippingDialogMode === 'create' ? 'Nuevo lugar de envío' : 'Editar lugar de envío'}
            </DialogTitle>
            <DialogDescription>
              Define el nombre y el estado de la ubicación para pedidos y logística.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="shipping-dialog-name">Nombre</Label>
              <Input
                id="shipping-dialog-name"
                className="mt-1"
                value={shippingDialogName}
                onChange={(event) => setShippingDialogName(event.target.value)}
                disabled={shippingSubmitting}
              />
            </div>
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <p className="text-sm text-gray-700">Activo</p>
              <Switch
                checked={shippingDialogActive}
                onCheckedChange={setShippingDialogActive}
                disabled={shippingSubmitting}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShippingDialogOpen(false)}
              disabled={shippingSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                void handleSubmitShipping();
              }}
              disabled={shippingSubmitting}
            >
              {shippingSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
